// Bon de commande imprimable (A4), à remettre au fournisseur.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { achatsAPI, settingsAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { useFer, kgParBarre, enBarres } from '../../utils/fer';

const css = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #e9ecef; font-family: Arial, Helvetica, sans-serif; color: #111; }
  .toolbar { position: sticky; top: 0; z-index: 2; background: #12284C; color: #fff; padding: 10px 16px; display: flex; gap: 12px; align-items: center; }
  .toolbar button { background: #fff; color: #12284C; border: 0; padding: 8px 14px; border-radius: 4px; font-weight: bold; cursor: pointer; }
  .page { width: 190mm; min-height: 277mm; margin: 12px auto; background: #fff; padding: 6mm; position: relative; display: flex; flex-direction: column; }
  .top { display: flex; justify-content: space-between; gap: 16px; }
  .em { font-size: 12px; width: 50%; }
  .em img { height: 60px; }
  .em .rs { font-weight: bold; font-size: 13px; margin: 6px 0; }
  .box { border: 1px solid #333; width: 48%; }
  .box .title { text-align: center; font-size: 22px; font-weight: bold; padding: 6px 0; border-bottom: 1px solid #333; }
  .box table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: center; }
  .box th { background: #e6e6e6; padding: 4px; border-bottom: 1px solid #333; }
  .box td { padding: 6px; }
  .four { border: 1px solid #333; border-radius: 8px; margin-top: 12px; padding: 10px 14px; font-size: 12.5px; line-height: 1.7; }
  .info { margin-top: 14px; font-size: 12px; display: flex; gap: 24px; flex-wrap: wrap; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12.5px; }
  table.lines th { background: #f0f0f0; border: 1px solid #333; padding: 5px; font-size: 12px; }
  table.lines td { border: 1px solid #333; padding: 5px; }
  .num { text-align: right; white-space: nowrap; }
  .totals { margin-left: auto; margin-top: 10px; border-collapse: collapse; font-size: 12.5px; }
  .totals td { border: 1px solid #333; padding: 5px 8px; }
  .totals tr:last-child td { font-weight: bold; }
  .sign { display: flex; justify-content: space-between; margin-top: 36px; font-size: 12px; }
  .sign div { width: 45%; border-top: 1px solid #999; padding-top: 6px; height: 70px; }
  .watermark { position: absolute; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-28deg); font-size: 90px; font-weight: bold; color: rgba(200, 0, 0, .12); white-space: nowrap; }
  @media print { body { background: #fff; } .toolbar { display: none; } .page { margin: 0; width: auto; padding: 0; } }
`;

const BCPrint = () => {
  const { id } = useParams();
  const [bc, setBc] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const ferT = useFer();
  useEffect(() => {
    Promise.all([achatsAPI.commande(id), settingsAPI.get()])
      .then(([b, st]) => { setBc(b.data); setS(st.data); document.title = b.data.numero || 'Bon de commande brouillon'; })
      .catch((e) => setError(apiError(e)));
  }, [id]);
  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!bc || !s) return <div style={{ padding: 24 }}>Chargement…</div>;
  const wm = bc.statut === 'BROUILLON' ? 'BROUILLON' : bc.statut === 'ANNULE' ? 'ANNULÉ' : null;
  const q3 = (n) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
  return (
    <>
      <style>{css}</style>
      <div className="toolbar"><strong>{bc.numero || 'Brouillon'}</strong><span style={{ marginRight: 'auto' }}>{bc.fournisseur}</span>
        <button onClick={() => window.print()}>Imprimer / PDF</button></div>
      <div className="page">
        {wm && <div className="watermark">{wm}</div>}
        <div className="top">
          <div className="em">
            <img src="/logo-docc.png" alt="DOCC" />
            <div className="rs">{s.raison_sociale}</div>
            <div>{s.adresse}</div>
            {s.telephone && <div>Tél : {s.telephone}</div>}
            {s.email && <div>Email : {s.email}</div>}
            {s.ninea && <div>NINEA : {s.ninea}</div>}
          </div>
          <div className="box">
            <div className="title">Bon de commande</div>
            <table><thead><tr><th>Numéro</th><th>Date</th><th>Livraison souhaitée</th></tr></thead>
              <tbody><tr><td>{bc.numero || '—'}</td><td>{dateFr(bc.date_commande)}</td><td>{bc.date_livraison_prevue ? dateFr(bc.date_livraison_prevue) : '—'}</td></tr></tbody></table>
            <div className="four" style={{ margin: 8 }}>
              <div><b>{bc.fournisseur}</b></div>
              {bc.f_contact && <div>À l'attention de : {bc.f_contact}</div>}
              {bc.f_adresse && <div>{bc.f_adresse}</div>}
              {bc.f_telephone && <div>Tél : {bc.f_telephone}</div>}
            </div>
          </div>
        </div>
        <div className="info">
          {bc.lieu_livraison && <span>Lieu de livraison : <b>{bc.lieu_livraison}</b></span>}
          {bc.conditions && <span>Conditions : <b>{bc.conditions}</b></span>}
        </div>
        <table className="lines">
          <thead><tr><th style={{ width: '6%' }}>N°</th><th>Désignation</th><th style={{ width: '10%' }}>Unité</th><th style={{ width: '11%' }}>Quantité</th><th style={{ width: '14%' }}>Prix unitaire HT</th><th style={{ width: '15%' }}>Montant HT</th></tr></thead>
          <tbody>
            {bc.lignes.map((l) => (
              <tr key={l.id}><td className="num">{l.position}</td><td>{l.designation}{(() => { const k = l.unite === 'kg' && l.intrant_code ? kgParBarre(l.intrant_code, null, ferT) : null; return k ? <span style={{ color: '#555', fontSize: 11 }}> — ≈ {String(enBarres(l.quantite, k)).replace('.', ',')} barres de {String(k).replace('.', ',')} kg</span> : null; })()}</td><td>{l.unite}</td>
                <td className="num">{q3(l.quantite)}</td><td className="num">{fcfa(l.prix_unitaire)}</td><td className="num">{fcfa(l.montant)}</td></tr>
            ))}
          </tbody>
        </table>
        <table className="totals"><tbody>
          <tr><td>Total HT</td><td className="num">{fcfa(bc.total_ht)} F</td></tr>
          <tr><td>TVA {bc.taux_tva} %</td><td className="num">{fcfa(bc.total_tva)} F</td></tr>
          <tr><td>Total TTC</td><td className="num">{fcfa(bc.total_ttc)} F</td></tr>
        </tbody></table>
        <div style={{ fontSize: 11.5, marginTop: 14 }}>Merci de rappeler le numéro de ce bon de commande sur votre bon de livraison et votre facture.</div>
        <div className="sign"><div>Le responsable des achats</div><div>Visa de la direction</div></div>
      </div>
    </>
  );
};

export default BCPrint;
